import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

function message(error, fallbackKey, fallback) {
    return getIntegrationErrorMessage(error) || i18next.t(fallbackKey, { defaultValue: fallback });
}

export class CollectionCampaignIntegration {
    constructor(service) {
        this.service = service;
    }

    async list() {
        try {
            return await this.service.list();
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.loadError', 'Could not load campaigns');
            toast.error(text);
            return { success: false, data: [], error: text };
        }
    }

    async targets() {
        try {
            return await this.service.targets();
        } catch (error) {
            return { success: false, data: [], error: message(error, 'collectionCampaigns:messages.loadError', 'Could not load record types') };
        }
    }

    async create(payload) {
        try {
            const result = await this.service.create(payload);
            toast.success(i18next.t('collectionCampaigns:messages.created', { defaultValue: 'Campaign saved' }));
            return result;
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.saveError', 'Could not save the campaign');
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async launch(id) {
        try {
            const result = await this.service.launch(id);
            toast.success(i18next.t('collectionCampaigns:messages.launched', { defaultValue: 'Tasks are being opened' }));
            return result;
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.launchError', 'Could not start the campaign');
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async resync(id) {
        try {
            const result = await this.service.resync(id);
            toast.success(i18next.t('collectionCampaigns:messages.resynced', { defaultValue: 'Audience updated' }));
            return result;
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.launchError', 'Could not refresh the audience');
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async cancel(id) {
        try {
            const result = await this.service.cancel(id);
            toast.success(i18next.t('collectionCampaigns:messages.cancelled', { defaultValue: 'Campaign cancelled' }));
            return result;
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.cancelError', 'Could not cancel the campaign');
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async remind(id) {
        try {
            const result = await this.service.remind(id);
            toast.success(i18next.t('collectionCampaigns:messages.reminded', { defaultValue: 'Reminder sent' }));
            return result;
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.remindError', 'Could not send reminders');
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async progress(id) {
        try {
            return await this.service.progress(id);
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.loadError', 'Could not load progress');
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async searchRecords(assignmentId, q) {
        try {
            return await this.service.searchRecords(assignmentId, q);
        } catch (error) {
            return { success: false, data: [], error: message(error, 'collectionCampaigns:messages.searchError', 'Search failed') };
        }
    }

    async createRecord(assignmentId, body) {
        try {
            const result = await this.service.createRecord(assignmentId, body);
            toast.success(i18next.t('collectionCampaigns:messages.recordSaved', { defaultValue: 'Record saved' }));
            return result;
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.recordError', 'Could not save the record');
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async listRecords(assignmentId) {
        try {
            return await this.service.listRecords(assignmentId);
        } catch (error) {
            return { success: false, data: [], error: message(error, 'collectionCampaigns:messages.recordError', 'Could not load records') };
        }
    }

    async updateRecord(assignmentId, recordId, record) {
        try {
            const result = await this.service.updateRecord(assignmentId, recordId, record);
            toast.success(i18next.t('collectionCampaigns:messages.recordUpdated', { defaultValue: 'Record updated' }));
            return result;
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.recordError', 'Could not update the record');
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async completeAssignment(assignmentId) {
        try {
            const result = await this.service.completeAssignment(assignmentId);
            toast.success(i18next.t('collectionCampaigns:messages.completed', { defaultValue: 'Task submitted' }));
            return result;
        } catch (error) {
            const raw = getIntegrationErrorMessage(error);
            const key = raw === 'Approval still pending'
                ? 'collectionCampaigns:messages.pendingApproval'
                : raw === 'Not enough records'
                    ? 'collectionCampaigns:messages.needMore'
                    : 'collectionCampaigns:messages.completeError';
            const text = i18next.t(key, { defaultValue: raw || 'Could not submit the task' });
            toast.error(text);
            return { success: false, error: text };
        }
    }

    async linkRecord(assignmentId, entityId) {
        try {
            const result = await this.service.linkRecord(assignmentId, entityId);
            toast.success(i18next.t('collectionCampaigns:messages.recordLinked', { defaultValue: 'Record linked to your task' }));
            return result;
        } catch (error) {
            const text = message(error, 'collectionCampaigns:messages.recordError', 'Could not link the record');
            toast.error(text);
            return { success: false, error: text };
        }
    }
}
